import type { ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { Route } from 'config';
import useCurrentUser from 'hooks/useCurrentUser';

import { Component as SwapIcon } from 'icons/icon-mktg--swap-shadow.svg';
import { Component as CloseIcon } from 'icons/icon-ui--close.svg';
import { Component as QuickEasyIcon } from 'icons/icon-mktg--quick-easy-shadow.svg';
import { Component as PauseIcon } from 'icons/icon-mktg--pause-shadow.svg';
import Modal from 'components/shared/Modal';

type Props = {
  show: boolean;
  reason: string;
  onClose: () => void;
  triggerDeactivation: (deactivationType: string) => void;
  showPauseSubscription: () => void;
};

function FlexBoxCancelRebuttalModal({
  show,
  reason,
  onClose,
  triggerDeactivation,
  showPauseSubscription,
}: Props): ReactElement {
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const { subscription } = currentUser;
  const triggerCancel = () => {
    onClose();
    triggerDeactivation('cancelled');
  };
  const triggerPause = () => {
    onClose();
    showPauseSubscription();
  };

  return (
    <Modal
      show={show}
      onClose={onClose}
      wrapperClassName="c-subscription-management-modal"
      className="modal-dialog-centered"
      body={(
        <>
          <button
            className="close"
            type="button"
            aria-label="Close modal"
            style={{
              padding: 0,
              border: 0,
            }}
            onClick={onClose}
          >
            <CloseIcon width="32" height="32" />
          </button>
          <br />
          <br />

          <div className="u-text u-text--center">
            {['dietary', 'recipes', 'flavor_taste'].includes(reason) && (
              <div>
                <SwapIcon width="136" />
                <br />
                <br />
                {subscription?.plan === 'four_servings'
                  && (
                    <>
                      <h2 className="c-heading c-heading--brand-5">
                        Wasn&apos;t the Menu
                        <br />
                        You Were Hoping For?
                      </h2>
                      <p>
                        We have more recipe choices, albeit with fewer servings available.
                        Check out the recipes in the 2 Serving Plan this week.
                      </p>
                      <button
                        className="c-button c-button--primary"
                        type="button"
                        onClick={() => navigate(Route.PROFILE_EDIT_PLAN)}
                        data-cy="modal-profile-edit-plan"
                      >
                        See Plans
                      </button>
                    </>
                  )}
              </div>
            )}
            {reason === 'my_own' && (
              <div>
                <PauseIcon width="80" />
                <br />
                <br />
                <h2 className="c-heading c-heading--brand-5">
                  Want to Pause Your Subscription
                  <br />
                  Rather Than Cancelling?
                </h2>
                <br />
                <button
                  className="c-button c-button--primary"
                  type="button"
                  onClick={triggerPause}
                  data-cy="modal-pause-subscription"
                >
                  Pause My Subscription
                </button>
              </div>
            )}
            {reason === 'cooking_time' && (
              <div>
                <QuickEasyIcon width="80" />
                <br />
                <br />
                <h2 className="c-heading c-heading--brand-5">
                  Your need for speed is a marvel!
                </h2>
                <p>
                  Are you sure? It really doesn&apos;t get any faster, tastier,
                  or plant-packed than these meals.
                </p>
                <br />
                <button
                  className="c-button c-button--primary"
                  type="button"
                  onClick={() => navigate(Route.WEEKLY_MENU_READY_TO_EAT)}
                  data-cy="modal-weekly-menu-prepared-meals"
                >
                  Take another look
                </button>
              </div>
            )}
            {[
              'need_variety',
              'portions_too_small',
              'less_meals',
              'not_kid_friendly',
            ].includes(reason) && (
              <div>
                <SwapIcon width="136" />
                <br />
                <br />
                {['need_variety', 'portions_too_small', 'less_meals'].includes(reason) && (
                  <>
                    <h2 className="c-heading c-heading--brand-5">
                      Want more variety?
                    </h2>
                    <p>
                      We get it, variety is the spice of life.
                      That&apos;s why we now offer 12 Meal Kits and 12 Ready To Eat Meals
                      {' '}
                      every week,
                      plus specialty grocery items.
                    </p>
                  </>
                )}
                {reason === 'not_kid_friendly' && (
                  <>
                    <h2 className="c-heading c-heading--brand-5">
                      You can&apos;t please everyone!
                    </h2>
                    <p>
                      But our newest menu offers 12 Meal Kits and 12 Ready To Eat Meals,
                      so we bet you can find something everyone likes.
                    </p>
                  </>
                )}
                <br />
                <button
                  className="c-button c-button--primary"
                  type="button"
                  onClick={() => navigate(Route.UPCOMING_MENUS)}
                  data-cy="modal-upcoming-menus"
                >
                  See menu
                </button>
              </div>
            )}
            <br />
            <button
              type="button"
              className="c-link"
              onClick={triggerCancel}
              style={{
                padding: 0,
                border: 0,
              }}
              data-cy="modal-cancel-subscription"
            >
              No thanks, I&apos;d like to cancel
            </button>
            <br />
            <br />
          </div>
        </>
      )}
    />
  );
}

export default FlexBoxCancelRebuttalModal;
